import { GraphColorsInOrder } from '@/config/settings';

/**
 * Visual constants for the generic metric charts.
 *
 * Colours are not here: the chrome (text, grid, axis, tooltip, zoom box) comes from the
 * chart theme (`../theme`), which reads the colour tokens for the applied theme. The series
 * palette below is the light `GraphColorsInOrder`; the builders pass every series colour
 * through `ChartTheme.color`, which swaps in the dark lift where one exists.
 */

/** Default series palette, in assignment order. */
export const METRIC_SERIES_COLORS: readonly string[] = GraphColorsInOrder;

export const CHART_GRID_DASH_PATTERN: [number, number] = [5, 5];
export const CHART_GRID_LINE_WIDTH = 2;
export const CHART_TRANSPARENT_COLOR = '#00000000';

/** Hides the axis tick marks while keeping an 8px margin off the axis border. */
export const CHART_AXIS_BORDER_MARGIN = {
    tickColor: CHART_TRANSPARENT_COLOR,
    tickLength: 8,
} as const;

export const CHART_LINE_WIDTH = 2;
export const CHART_LINE_TENSION = 0.4;
/**
 * Half the line width, so a point on a drawn line is exactly as thick as the line
 * and disappears into it. It used to be 0; at that radius a dense series looks
 * identical, but a day whose neighbours are both missing rendered *nothing at all*
 * — see `CHART_ISOLATED_POINT_RADIUS`.
 */
export const CHART_POINT_RADIUS = CHART_LINE_WIDTH / 2;
/**
 * Points on a series drawn without a connecting line have to be visible — with
 * a line-width radius a scatter series would be a row of specks.
 */
export const CHART_SCATTER_POINT_RADIUS = 3;
/**
 * A measurement with no measurement either side of it draws no line segment, so it
 * is the point or nothing. It is given the scatter radius because that is what it
 * is: a lone reading with nothing to connect to.
 *
 * This cannot be a single radius for every point. Raising the base radius to 2
 * makes isolated days readable but beads a long dense series into a scalloped
 * band, so the radius has to depend on whether the point has a neighbour.
 */
export const CHART_ISOLATED_POINT_RADIUS = CHART_SCATTER_POINT_RADIUS;
export const CHART_POINT_HOVER_RADIUS = 4;
export const CHART_POINT_BORDER_WIDTH = 0;
export const CHART_POINT_HOVER_BORDER_WIDTH = 2;

/** Dash pattern applied to series flagged `dashed` (derived trend lines). */
export const CHART_SERIES_DASH_PATTERN: [number, number] = [6, 4];

/**
 * A bucket still in progress (`MetricChartSpec.inProgress`). Marked by weight and outline
 * rather than by a second colour, which would read as a different series or, on a
 * threshold-coloured bar, as a different band.
 */
export const CHART_IN_PROGRESS_FILL_FACTOR = 0.35;
export const CHART_IN_PROGRESS_BAR_BORDER_WIDTH = 2;
export const CHART_IN_PROGRESS_POINT_RADIUS = CHART_POINT_HOVER_RADIUS;
export const CHART_IN_PROGRESS_POINT_BORDER_WIDTH = 2;

/** Fill opacity for a series flagged `fill` that does not set its own. */
export const DEFAULT_FILL_OPACITY = 0.2;

export const DEFAULT_AXIS_DECIMALS = 1;
export const DEFAULT_MAX_TICKS_LIMIT = 6;
/**
 * The Chart.js default tick limit. A chart that drew its own scale before it moved onto the
 * metric chart, and so had this finer scale, keeps it by naming it.
 */
export const CHARTJS_MAX_TICKS_LIMIT = 11;

/**
 * Picks a palette colour by index, wrapping around when a chart has more series
 * than the palette has entries.
 */
export function seriesColorAt(index: number): string {
    return METRIC_SERIES_COLORS[index % METRIC_SERIES_COLORS.length];
}

/**
 * Chart.js scale id for an axis reference.
 *
 * `left` and `right` keep their fixed ids so existing specs are untouched; any other
 * value is a key into `spec.extraAxes` and gets a namespaced id. One function so the
 * data and options builders cannot disagree about where a series is plotted.
 */
export function axisScaleId(axis: string | undefined): string {
    if (axis === 'right') return 'yAxisRight';
    if (!axis || axis === 'left') return 'yAxisLeft';
    return `yAxis-${axis}`;
}
